import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, MenuItem, Paper, Radio, RadioGroup, Stack,
    TextField, Typography
} from '@mui/material';
import { API_BASE_URL, fetchJson } from './api';
import { ReadableValue } from './DataIntroductionProposal';
import { conflictContext, conflictExplanation, conflictTitle, fieldLabel, processingMessage, ReportDataSource } from './ReportDataPresentation';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DescriptionIcon from '@mui/icons-material/Description';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SaveIcon from '@mui/icons-material/Save';

export function QuestionnairePreflightDialog({ reference, reportInstance, authHeaders, onCancel, onDraft, onUpload, onEnter }) {
    const [snapshot, setSnapshot] = useState(null);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [visits, setVisits] = useState([]);
    const [canDefer, setCanDefer] = useState(false);
    const [deferral, setDeferral] = useState(null);
    const [reason, setReason] = useState('');
    const [owner, setOwner] = useState('');
    const [visitId, setVisitId] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const epoch = useRef(0);
    const pendingSave = useRef(false);
    const completed = useRef(false);
    const callbacks = useRef({ authHeaders, onDraft });
    callbacks.current = { authHeaders, onDraft };
    useEffect(() => {
        let cancelled = false;
        const version = ++epoch.current;
        completed.current = false;
        setSnapshot(null); setError('');
        setDeferral(null); setSaved(false); setSaving(false); pendingSave.current = false;
        setCanDefer(false); setVisits([]); setVisitId('');
        fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/questionnaire-completeness?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`,
            { headers: callbacks.current.authHeaders?.(), cache: 'no-store' }).then((result) => {
            const value = result.completeness;
            if (cancelled) return;
            if (!result.ok || !Array.isArray(value?.questions) || !Array.isArray(value?.outstanding) || typeof value.complete !== 'boolean') throw new Error('Completeness check returned an invalid response');
            setSnapshot(value);
            setVisits(Array.isArray(result.visits) ? result.visits : []);
            setCanDefer(result.deferral_available === true);
            if (value.complete && !completed.current) { completed.current = true; callbacks.current.onDraft(); }
        }).catch((failure) => { if (!cancelled) setError(failure.message || 'Completeness could not be checked'); });
        return () => { cancelled = true; epoch.current = version + 1; };
    }, [reference, reportInstance, retry]);
    const saveDeferral = async () => {
        if (!deferral || !reason.trim() || !visitId || pendingSave.current) return;
        const version = epoch.current;
        pendingSave.current = true; setSaving(true); setError(''); setSaved(false);
        try {
            const result = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/questionnaire-completeness/defer`, {
                method: 'POST', headers: callbacks.current.authHeaders?.({ 'Content-Type': 'application/json' }) || { 'Content-Type': 'application/json' },
                body: JSON.stringify({ request_id: deferral.request_id, report_instance: reportInstance, visit_id: Number(visitId),
                    field_id: deferral.field_id, schema_version: snapshot.schema_version, reason: reason.trim(), ...(owner.trim() ? { owner_name: owner.trim() } : {}) })
            });
            if (epoch.current !== version) return;
            if (!result.ok || !Array.isArray(result.completeness?.questions) || !Array.isArray(result.completeness?.outstanding)
                || typeof result.completeness.complete !== 'boolean') throw new Error('The deferral save could not be confirmed');
            setSnapshot(result.completeness); setDeferral(null); setSaved(true);
        } catch (failure) { if (epoch.current === version) setError(failure.message || 'The deferral save failed'); }
        finally { if (epoch.current === version) { setSaving(false); pendingSave.current = false; } }
    };
    const requirementsByField = new Set((snapshot?.outstanding || []).map((item) => item.field_id));
    const items = [...(snapshot?.questions || []).filter((item) => !requirementsByField.has(item.field_id)), ...(snapshot?.outstanding || [])];
    return <Dialog open onClose={onCancel} fullWidth maxWidth="sm" aria-labelledby="questionnaire-preflight-title">
        <DialogTitle id="questionnaire-preflight-title">Outstanding Report Information</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">Deferral saved. The information remains outstanding.</Alert>}
            {!snapshot && !error && <CircularProgress size={22} aria-label="Checking questionnaire completeness" />}
            {snapshot && <>
                <Typography variant="body2">Unanswered questionnaire items: {snapshot.questions.length}. Outstanding information: {snapshot.outstanding.length}.</Typography>
                {items.map((item) => <Box key={item.id || `${item.field_id}:${item.visit_id || ''}`} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{item.description || item.question}</Typography>
                    <Typography variant="caption">{item.status}{item.owner_name ? ` / ${item.owner_name}` : ''}</Typography>
                    {canDefer && visits.length > 0 && item.status !== 'deferred' && <Button size="small" startIcon={<SkipNextIcon />}
                        disabled={saving} aria-label={`Defer ${item.description || item.question}`} onClick={() => {
                            setDeferral({ field_id: item.field_id, key: item.id || item.field_id, request_id: crypto.randomUUID() });
                            setReason(''); setOwner(''); setVisitId(''); setError(''); setSaved(false);
                        }}>Defer</Button>}
                    {deferral?.key === (item.id || item.field_id) && <Stack spacing={1} sx={{ mt: 1 }}>
                        <TextField select size="small" label="Deferral visit" value={visitId} disabled={saving} onChange={(event) => setVisitId(event.target.value)}>
                            {visits.map((visit) => <MenuItem key={visit.id} value={String(visit.id)} sx={{ whiteSpace: 'normal' }}>
                                Visit {visit.id}: {new Date(visit.starts_at).toLocaleString('en-GB')} / {visit.status}
                            </MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Deferral reason" value={reason} disabled={saving} multiline minRows={2}
                            inputProps={{ maxLength: 1000 }} onChange={(event) => setReason(event.target.value)} />
                        <TextField size="small" label="Follow-up owner" value={owner} disabled={saving} inputProps={{ maxLength: 200 }} onChange={(event) => setOwner(event.target.value)} />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button startIcon={<SaveIcon />} disabled={saving || !reason.trim() || !visitId} onClick={saveDeferral}>Save deferral</Button>
                            <Button disabled={saving} onClick={() => setDeferral(null)}>Cancel deferral</Button>
                        </Stack>
                    </Stack>}
                </Box>)}
                <Alert severity="info">Outstanding documentary information does not prevent a draft. Approval and issue remain separate.</Alert>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button startIcon={<UploadFileIcon />} disabled={saving} onClick={onUpload}>Upload</Button>
                    <Button startIcon={<EditNoteIcon />} disabled={saving} onClick={onEnter}>Enter information</Button>
                </Stack>
            </>}
        </Stack></DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={onCancel}>Cancel</Button>
            {error && <Button disabled={saving} onClick={() => setRetry((value) => value + 1)}>Retry check</Button>}
            <Button startIcon={<DescriptionIcon />} variant="contained" disabled={!snapshot || !!error || saving} onClick={() => {
                if (!completed.current) { completed.current = true; onDraft(); }
            }}>Generate draft with outstanding items</Button>
        </DialogActions>
    </Dialog>;
}

export function reconciliationReady(state) {
    return state?.ok === true && state.ready === true
        && Array.isArray(state.conflicts) && state.conflicts.every((conflict) => conflict.status === 'resolved')
        && Array.isArray(state.issues) && !state.issues.some((issue) => issue.blocking);
}

export function isReportDataReconciliationError(error) {
    return Number(error?.status) === 409
        && ['REPORT_DATA_CONFLICTS', 'REPORT_DATA_PROCESSING_REQUIRED'].includes(error?.body?.code);
}

function displayValue(value) {
    if (value == null || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value ?? '');
}

function valueType(conflict) {
    const type = conflict.value_type || conflict.choices?.[0]?.value_type;
    if (['number', 'integer', 'decimal', 'float'].includes(type)) return 'number';
    if (['boolean', 'bool'].includes(type)) return 'boolean';
    if (['object', 'array', 'null'].includes(type)) return type;
    const value = conflict.choices?.[0]?.value;
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    if (['number', 'boolean', 'object'].includes(typeof value)) return typeof value;
    return 'string';
}

function correctedValue(type, value) {
    if (type === 'null') return null;
    if (!value.trim()) throw new Error('Enter a corrected value.');
    if (type === 'number') {
        const number = Number(value);
        if (!Number.isFinite(number)) throw new Error('Enter a finite number.');
        return number;
    }
    if (type === 'boolean') {
        if (!['true', 'false'].includes(value)) throw new Error('Choose true or false.');
        return value === 'true';
    }
    if (type === 'object' || type === 'array') {
        let parsed;
        try { parsed = JSON.parse(value); } catch { throw new Error('Enter valid JSON for the corrected value.'); }
        if (type === 'array' ? !Array.isArray(parsed) : !parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error(`Enter a JSON ${type}.`);
        }
        return parsed;
    }
    return value;
}

function validateState(state) {
    if (state?.ok !== true || !Array.isArray(state.conflicts) || !Array.isArray(state.issues) || typeof state.ready !== 'boolean') {
        throw new Error('The report data check returned an invalid response. Retry before generating.');
    }
    return state;
}

export default function ReportDataConflictDialog({
    open, reference, reportInstance, initialState, authHeaders, onCancel, onReady
}) {
    const [state, setState] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const epoch = useRef(0);
    const completed = useRef(false);
    const saving = useRef(false);
    const callbacks = useRef({ onReady, authHeaders });
    callbacks.current = { onReady, authHeaders };
    const endpoint = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/report-data-reconciliation`;
    const unresolvedConflicts = (state?.conflicts || []).filter((conflict) => conflict.status !== 'resolved');

    function headers(extra = {}) {
        return callbacks.current.authHeaders ? callbacks.current.authHeaders(extra) : extra;
    }

    function applyState(next, allowContinue) {
        validateState(next);
        setState(next);
        setDrafts({});
        if (allowContinue && reconciliationReady(next) && !completed.current) {
            completed.current = true;
            callbacks.current.onReady();
        }
    }

    useEffect(() => {
        if (!open) return;
        const requestEpoch = ++epoch.current;
        completed.current = false;
        saving.current = false;
        setState(null);
        setDrafts({});
        setError('');
        setBusy(true);
        const load = initialState
            ? Promise.resolve(initialState)
            : fetchJson(`${endpoint}?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`, {
                headers: callbacks.current.authHeaders?.(), cache: 'no-store'
            });
        load.then((next) => {
            if (epoch.current !== requestEpoch) return;
            validateState(next);
            setState(next);
            // A generation race must be reviewed rather than immediately retrying generation.
            if (!initialState && reconciliationReady(next) && !completed.current) {
                completed.current = true;
                callbacks.current.onReady();
            }
        }).catch((failure) => {
            if (epoch.current === requestEpoch) setError(failure.message || 'Could not check report data.');
        }).finally(() => {
            if (epoch.current === requestEpoch) setBusy(false);
        });
        return () => { epoch.current += 1; };
    }, [open, endpoint, reportInstance, initialState]);

    function cancel() {
        epoch.current += 1;
        completed.current = true;
        onCancel();
    }

    async function refresh() {
        const requestEpoch = epoch.current;
        setBusy(true);
        setError('');
        try {
            const next = await fetchJson(`${endpoint}?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`, {
                headers: headers(), cache: 'no-store'
            });
            if (epoch.current === requestEpoch) applyState(next, true);
        } catch (failure) {
            if (epoch.current === requestEpoch) {
                setState(null);
                setError(failure.message || 'Could not check report data.');
            }
        } finally {
            if (epoch.current === requestEpoch) setBusy(false);
        }
    }

    async function processSourceAgain(issue) {
        if (saving.current) return;
        const requestEpoch = epoch.current;
        saving.current = true;
        setBusy(true);
        setError('');
        try {
            await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/data-intakes/${encodeURIComponent(issue.run_id)}/process?mode=surveyor`, {
                method: 'POST', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify({})
            });
            if (epoch.current !== requestEpoch) return;
            const next = await fetchJson(`${endpoint}?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`, {
                headers: headers(), cache: 'no-store'
            });
            if (epoch.current === requestEpoch) applyState(next, true);
        } catch (failure) {
            if (epoch.current === requestEpoch) {
                setState((previous) => previous ? { ...previous, ready: false } : null);
                setError(failure.message || 'Source processing could not be completed. Retry the data check before generating.');
            }
        } finally {
            if (epoch.current === requestEpoch) {
                saving.current = false;
                setBusy(false);
            }
        }
    }

    function updateDraft(conflict, change) {
        setDrafts((previous) => ({
            ...previous,
            [conflict.id]: { type: valueType(conflict), ...previous[conflict.id], ...change }
        }));
    }

    async function save(conflict) {
        if (saving.current) return;
        const draft = drafts[conflict.id] || {};
        setError('');
        const body = {
            report_instance: reportInstance, conflict_id: conflict.id,
            candidate_hash: conflict.candidate_hash, reason: (draft.reason || '').trim()
        };
        try {
            if (draft.selection === 'corrected') body.corrected_value = correctedValue(draft.type, draft.value || '');
            else if (draft.selection === 'unknown') body.unknown = true;
            else {
                const choice = conflict.choices.find((item) => `choice:${item.id}` === draft.selection);
                if (!choice?.candidate_ids?.length) throw new Error('Choose a value, provide a correction, or explicitly mark unknown.');
                body.selected_candidate_ids = choice.candidate_ids;
            }
        } catch (failure) {
            setError(failure.message);
            return;
        }
        saving.current = true;
        setBusy(true);
        const requestEpoch = epoch.current;
        try {
            const next = await fetchJson(`${endpoint}/decisions?mode=surveyor`, {
                method: 'POST', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(body)
            });
            if (epoch.current === requestEpoch) applyState(next, true);
        } catch (failure) {
            if (epoch.current !== requestEpoch) return;
            if (Number(failure.status) === 409) {
                setError('The source data changed. Review the refreshed values and save a new decision.');
                try {
                    const next = failure.body?.reconciliation || await fetchJson(
                        `${endpoint}?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`,
                        { headers: headers(), cache: 'no-store' }
                    );
                    if (epoch.current === requestEpoch) applyState(next, false);
                } catch (refreshFailure) {
                    if (epoch.current === requestEpoch) {
                        setState(null);
                        setError(refreshFailure.message || 'Could not refresh changed report data.');
                    }
                }
            } else setError(failure.message || 'The decision could not be saved. Retry before generating.');
        } finally {
            if (epoch.current === requestEpoch) {
                saving.current = false;
                setBusy(false);
            }
        }
    }

    return (
        <Dialog open={open} onClose={cancel} fullWidth maxWidth="md" aria-labelledby="report-data-conflict-title">
            <DialogTitle id="report-data-conflict-title">Review report data before generation</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography>Choose the report value using the source evidence shown beneath it. No source is preferred automatically. Resolve every conflict for {reportInstance === 'initial' ? 'the Initial Report' : `Interim Report ${reportInstance.split(':')[1]}`}. Decisions apply to report data only; source forms are unchanged.</Typography>
                    {state && !state.ready && <Typography variant="body2" color="text.secondary">Generation remains on hold until the outstanding reviews and processing checks are complete.</Typography>}
                    {busy && <Box role="status"><CircularProgress size={20} /> Checking or saving report data…</Box>}
                    {error && <Alert severity="error">{error}</Alert>}
                    {state?.issues.map((issue, index) => {
                        const intakeType = String(issue.intake_type || '').toLowerCase();
                        const emailSource = intakeType.includes('email') || intakeType === 'gmail';
                        const canProcess = issue.code === 'INTAKE_PROCESSING_REQUIRED'
                            && issue.run_id != null && intakeType && !emailSource
                            && issue.status !== 'processing';
                        return (
                            <Alert key={issue.id || index} severity={issue.blocking ? 'warning' : 'info'}>
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        {processingMessage(issue) || 'This source needs review before it can be used in the report.'}
                                    </Typography>
                                    {emailSource && <Typography variant="body2">Start a new Email Search in this report workspace and give processing consent. Inbox searches are never reprocessed automatically.</Typography>}
                                    {canProcess && <Button disabled={busy} onClick={() => processSourceAgain(issue)} variant="outlined">Process source again</Button>}
                                </Stack>
                            </Alert>
                        );
                    })}
                    {state && !state.ready && !unresolvedConflicts.length && !state.issues.some((issue) => issue.blocking) && (
                        <Alert severity="warning">Report data is not ready. Refresh the check before generating.</Alert>
                    )}
                    {unresolvedConflicts.map((conflict, index) => {
                        const draft = drafts[conflict.id] || {};
                        const titleId = `report-conflict-${index}`;
                        const sourceProcessing = conflict.kind === 'source_processing';
                        return (
                            <Paper key={conflict.id} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, overflowWrap: 'anywhere' }}>
                                <Stack spacing={1.5}>
                                    <Box>
                                        {conflictContext(conflict) && <Typography variant="caption" color="text.secondary">{conflictContext(conflict)}</Typography>}
                                        <Typography id={titleId} variant="h6">{conflictTitle(conflict, state.issues)}</Typography>
                                    </Box>
                                    {conflictExplanation(conflict) && <Typography variant="body2" color="text.secondary">{conflictExplanation(conflict)}</Typography>}
                                    {sourceProcessing && <Alert severity="warning">Excluding this unprocessed source removes it from report data and records a report limitation. It does not process or verify the source. You may add an explanatory note, or process the source above instead.</Alert>}
                                    <RadioGroup aria-labelledby={titleId} value={draft.selection || ''} onChange={(event) => updateDraft(conflict, { selection: event.target.value })}>
                                        {conflict.choices.map((choice, choiceIndex) => (
                                            <Box key={choice.id} sx={{ mb: 1.5, p: { xs: 1.5, sm: 2 }, border: '1px solid', borderRadius: 1, borderColor: draft.selection === `choice:${choice.id}` ? 'primary.main' : 'divider', bgcolor: draft.selection === `choice:${choice.id}` ? 'action.selected' : 'background.paper', minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">Report value / Option {choiceIndex + 1}</Typography>
                                                <FormControlLabel disabled={busy} value={`choice:${choice.id}`} control={<Radio />} sx={{ display: 'flex', alignItems: 'flex-start', m: 0, '.MuiFormControlLabel-label': { minWidth: 0, flex: 1, fontWeight: 600, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }, '.MuiTypography-body2': { fontWeight: 600 } }} label={
                                                    typeof choice.value === 'object' && choice.value !== null
                                                        ? <Box><ReadableValue value={choice.value} />{choice.unit && <Typography variant="body2">{choice.unit}</Typography>}</Box>
                                                        : `${displayValue(choice.value)}${choice.unit ? ` ${choice.unit}` : ''}`
                                                } />
                                                {(choice.basis || choice.currency || choice.vat_basis) && <Typography variant="body2" color="text.secondary">
                                                    {[choice.basis && `Measurement basis: ${choice.basis}`, choice.currency && `Currency: ${choice.currency}`, choice.vat_basis && `VAT: ${fieldLabel(choice.vat_basis)}`].filter(Boolean).join(' / ')}
                                                </Typography>}
                                                <Divider sx={{ my: 1.5 }} />
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Source evidence</Typography>
                                                <Stack spacing={1.5}>
                                                    {(choice.sources || []).map((source, sourceIndex) => (
                                                        <ReportDataSource key={sourceIndex} source={source} />
                                                    ))}
                                                    {!choice.sources?.length && <Typography variant="body2" color="text.secondary">Source details were not provided.</Typography>}
                                                </Stack>
                                            </Box>
                                        ))}
                                        {!sourceProcessing && <FormControlLabel disabled={busy} value="corrected" control={<Radio />} label="Provide a corrected value" />}
                                        <FormControlLabel disabled={busy} value="unknown" control={<Radio />} label={sourceProcessing ? 'Exclude source for this report' : 'Explicitly mark unknown'} />
                                    </RadioGroup>
                                    {draft.selection === 'corrected' && (
                                        <Stack spacing={1}>
                                            <TextField select label="Corrected value type" value={draft.type} disabled={busy} onChange={(event) => updateDraft(conflict, { type: event.target.value, value: '' })}>
                                                {['string', 'number', 'boolean', 'object', 'array', 'null'].map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                            </TextField>
                                            {draft.type !== 'null' && (
                                                <TextField label="Corrected value" disabled={busy} value={draft.value || ''} select={draft.type === 'boolean'} type={draft.type === 'number' ? 'number' : 'text'} multiline={['object', 'array'].includes(draft.type)} onChange={(event) => updateDraft(conflict, { value: event.target.value })}>
                                                    {draft.type === 'boolean' ? ['true', 'false'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>) : null}
                                                </TextField>
                                            )}
                                        </Stack>
                                    )}
                                    <TextField label="Reason for decision (optional)" value={draft.reason || ''} multiline minRows={2} disabled={busy} onChange={(event) => updateDraft(conflict, { reason: event.target.value })} />
                                    <Button variant="contained" disabled={busy || !draft.selection} onClick={() => save(conflict)}>Save decision</Button>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap' }}>
                <Button onClick={cancel}>Cancel generation</Button>
                <Button disabled={busy} onClick={refresh}>Retry check</Button>
                {reconciliationReady(state) && <Button disabled={busy} variant="contained" onClick={() => {
                    if (!completed.current) { completed.current = true; callbacks.current.onReady(); }
                }}>Continue generation</Button>}
            </DialogActions>
        </Dialog>
    );
}
